import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Button, Card, DataTable, EmptyState, FilterBar, FilterChips, KpiCard, Pagination,
  type Column, fmtBRL,
} from "@atlas/ui/web";
import { motivoVazio, POR_PAGINA_PADRAO, recortar } from "../../lib/recorte";
import { compParaInput, filtrarFolhas } from "../../lib/filtro-folhas";
import { useFiltros } from "../../lib/use-filtros";
import { atlas } from "../../lib/sdk";
import { useUnidadeAtiva } from "../../lib/unidade-ativa";
import { PageHeader, fmtDataISO, inp } from "./_ui";
import { downloadCsv, printScope } from "../../lib/export-relatorio";

/** O `inp` compartilhado tem `width: 100%`, que dentro da barra em linha faria
 *  cada campo de mês ocupar a faixa inteira. */
const ESTILO_MES = { ...inp, width: "auto" };


export function PrefeituraRelatorios() {
  // A organizacao sobre a qual esta tela opera — a do login, ou a atendida
  // escolhida no seletor do cabecalho. Fora do shell (teste isolado) devolve
  // vazio, e a tela se comporta como antes da feature.
  const { chaveDaUnidade, opcoesDoSdk } = useUnidadeAtiva();
  const u = chaveDaUnidade();
  const o = opcoesDoSdk();
  const vinc = useQuery({ queryKey: ["prefeitura", "rel-vinculo", ...u], queryFn: () => atlas.prefeitura.relServidoresPorVinculo(o) });
  const margem = useQuery({ queryKey: ["prefeitura", "rel-margem", ...u], queryFn: () => atlas.prefeitura.relMargemMedia(o) });
  const banco = useQuery({ queryKey: ["prefeitura", "rel-banco", ...u], queryFn: () => atlas.prefeitura.relContratosPorBanco(o) });
  const folhas = useQuery({ queryKey: ["prefeitura", "folhas", ...u], queryFn: () => atlas.prefeitura.folhas(o) });
  const [pagFolhas, setPagFolhas] = useState(1);
  const [ppFolhas, setPpFolhas] = useState(POR_PAGINA_PADRAO);

  // Recorte do Historico de folhas — intervalo de competencia + situacao.
  //
  // Mesma consulta que a aba Competencias de Troca de arquivos, e por isso o
  // filtro mora em `lib/filtro-folhas`: sao duas leituras do mesmo ciclo (aqui
  // conferencia, la' execucao), e duas copias do recorte divergiriam na
  // primeira mudanca de regra.
  const [compIni, setCompIni] = useState("");
  const [compFim, setCompFim] = useState("");
  const [statusFolha, setStatusFolha] = useState("");
  const todasFolhas = folhas.data?.folhas ?? [];
  const recorteFolhas = filtrarFolhas(todasFolhas, { compIni, compFim, status: statusFolha });

  const filtrosFolhas = useFiltros(
    [
      { nome: "competencia", ligado: compIni !== "" || compFim !== "", limpar: () => { setCompIni(""); setCompFim(""); } },
      { nome: "status", ligado: statusFolha !== "", limpar: () => setStatusFolha("") },
    ],
    setPagFolhas,
  );

  const folhasPag = recortar(recorteFolhas.filtradas, pagFolhas, ppFolhas);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <PageHeader title="Relatórios operacionais" subtitle="Servidores por vínculo, margem média, contratos por consignatária e histórico de folhas. Cada seção pode ser exportada em Excel ou PDF." />

      <div style={{ display: "grid", gap: 16, gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))" }}>
        <KpiCard label="Servidores" value={margem.data?.servidores ?? 0} />
        <KpiCard label="Margem média total" value={fmtBRL(margem.data?.margemMediaTotal ?? 0)} accent="info" />
        <KpiCard label="Margem média disp." value={fmtBRL(margem.data?.margemMediaDisponivel ?? 0)} accent="success" />
        <KpiCard label="% uso médio" value={`${((margem.data?.percentualUsoMedio ?? 0) * 100).toFixed(1)}%`} />
      </div>

      <div style={{ display: "grid", gap: 16, gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))" }}>
        <Card>
          <div data-print-scope="rel-vinculo">
            <SecaoHeader
              titulo="Servidores por vínculo"
              // A rota conta sobre o cadastro ATUAL desta prefeitura — nao ha
              // competencia nem corte temporal. Rotular como "período" seria
              // inventar uma dimensao que o dado nao tem.
              abrangencia="Posição atual do cadastro. Uma linha por vínculo; Total = servidores cadastrados hoje nesta prefeitura, sem recorte de competência."
              onExcel={() => downloadCsv(
                "relatorio-servidores-por-vinculo",
                vinc.data?.dados ?? [],
                [{ key: "vinculo", label: "Vínculo" }, { key: "total", label: "Total" }],
              )}
              onPdf={() => printScope("rel-vinculo", "Servidores por vínculo")}
            />
            <DataTable
              columns={[{ key: "vinculo", header: "Vínculo" }, { key: "total", header: "Total", align: "right" }] as Column<{ vinculo: string; total: number }>[]}
              rows={vinc.data?.dados ?? []}
              rowKey={(r) => r.vinculo}
              loading={vinc.isLoading}
              emptyState="Nenhum servidor cadastrado ainda."
            />
          </div>
        </Card>
        <Card>
          <div data-print-scope="rel-banco">
            <SecaoHeader
              titulo="Contratos por consignatária"
              // O universo e' a regra `situacaoContaComoAverbado` do servidor —
              // a MESMA do "volume averbado" da averbadora. Antes contava tudo
              // (proposta, reserva, cancelado, cartao) e nao batia com a
              // averbadora nem com a margem; o cliente reportou em 24/07/2026.
              // A tela nunca disse isso, entao o numero certo parecia errado.
              abrangencia="Posição atual, só contratos averbados (ativo, averbado ou quitado). Contratos = quantidade; Parcelas = soma da parcela mensal viva — quitado conta na quantidade e entra como zero no valor."
              onExcel={() => downloadCsv(
                "relatorio-contratos-por-consignataria",
                banco.data?.dados ?? [],
                [
                  { key: "banco", label: "Consignatária" },
                  { key: "contratos", label: "Contratos" },
                  { key: "valorParcela", label: "Parcelas (R$)", format: (v) => Number(v ?? 0).toFixed(2) },
                ],
              )}
              onPdf={() => printScope("rel-banco", "Contratos por consignatária")}
            />
            <DataTable
              columns={[
                { key: "banco", header: "Consignatária" },
                { key: "contratos", header: "Contratos", align: "right" },
                { key: "valorParcela", header: "Parcelas", align: "right", render: (r: { valorParcela: number }) => fmtBRL(r.valorParcela) },
              ] as Column<{ banco: string; contratos: number; valorParcela: number }>[]}
              rows={banco.data?.dados ?? []}
              rowKey={(r) => r.banco}
              loading={banco.isLoading}
              emptyState="Nenhum contrato averbado ainda."
            />
          </div>
        </Card>
      </div>

      <Card>
        <div data-print-scope="rel-folhas">
          <SecaoHeader
            titulo="Histórico de folhas processadas"
            onExcel={() => downloadCsv(
              "relatorio-folhas",
              folhas.data?.folhas ?? [],
              [
                { key: "competencia", label: "Competência" },
                { key: "dataCorte", label: "Corte", format: (v) => fmtDataISO(v as string) },
                { key: "dataRepasse", label: "Repasse", format: (v) => fmtDataISO(v as string | null) },
                { key: "status", label: "Status" },
                { key: "resultados", label: "Resultados" },
              ],
            )}
            onPdf={() => printScope("rel-folhas", "Histórico de folhas")}
          />

          {/* Ate 04/09/2026 este historico so' paginava, e ele ganha uma linha
              por mes para sempre — achar a competencia de marco era virar
              pagina. Sem busca textual: competencia e situacao sao dimensoes
              tipadas e nao ha identificador para procurar aqui.
              A EXPORTACAO segue exportando `folhas.data` INTEIRO, nao o
              recorte, e isso e' deliberado: o botao diz "Histórico de folhas",
              e um CSV que mudasse de tamanho conforme o filtro da tela viraria
              relatorio irreprodutivel. Quem quer o recorte le a tabela. */}
          {todasFolhas.length > 0 ? (
            <FilterBar
              layout="linha"
              {...filtrosFolhas.propsFilterBar}
              actions={
                <FilterChips
                  variant="segmentado"
                  label="Situação"
                  value={statusFolha}
                  onChange={filtrosFolhas.filtrar(setStatusFolha)}
                  options={[
                    { value: "", label: "Todas", count: recorteFolhas.totalSemStatus },
                    ...recorteFolhas.contagemStatus.map((s) => ({ value: s.valor, label: s.valor, count: s.contagem })),
                  ]}
                />
              }
            >
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--text-muted)" }}>
                De
                <input type="month" value={compParaInput(compIni)} onChange={(e) => filtrosFolhas.filtrar(setCompIni)(e.target.value)} style={ESTILO_MES} />
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--text-muted)" }}>
                até
                <input type="month" value={compParaInput(compFim)} onChange={(e) => filtrosFolhas.filtrar(setCompFim)(e.target.value)} style={ESTILO_MES} />
              </label>
            </FilterBar>
          ) : null}

          <DataTable
            columns={[
              { key: "competencia", header: "Competência", mono: true },
              { key: "dataCorte", header: "Corte", render: (f: { dataCorte: string }) => fmtDataISO(f.dataCorte) },
              { key: "dataRepasse", header: "Repasse", render: (f: { dataRepasse: string | null }) => fmtDataISO(f.dataRepasse) },
              { key: "status", header: "Status" },
              { key: "resultados", header: "Resultados", align: "right" },
] as Column<{ competencia: string; dataCorte: string; dataRepasse: string | null; status: string; resultados: number }>[]}
            rows={folhasPag.itens}
            rowKey={(f) => f.competencia}
            loading={folhas.isLoading}
            emptyState={
              folhas.isError
                ? "Não foi possível carregar o histórico de folhas."
                : <EmptyState
                    motivo={motivoVazio(todasFolhas.length, recorteFolhas.filtradas.length)}
                    filtroVazio="Nenhuma folha nesse período ou situação."
                    semDados="Nenhuma folha processada ainda."
                    onLimpar={filtrosFolhas.limparFiltros}
                  />
            }
          />
          <Pagination total={folhasPag.total} pagina={folhasPag.pagina} porPagina={ppFolhas} onPagina={setPagFolhas} onPorPagina={setPpFolhas} rotulo="folhas" />
        </div>
      </Card>

    </div>
  );
}

/** Cabecalho de secao com titulo + botoes Excel/PDF. Cliente pediu 31/07/2026:
 *  todo relatorio da prefeitura tem que poder ser exportado nesses dois formatos. */
/**
 * Cabecalho de secao com titulo, ABRANGENCIA e botoes Excel/PDF.
 *
 * `abrangencia` diz de que conjunto o numero fala, e existe porque sem ela um
 * agregado e' incomparavel: "Contratos por banco: 42" nao diz se conta
 * proposta, cancelado e cartao ou so' o que esta averbado — e as duas leituras
 * levam a conclusoes opostas sobre a carteira. A regra vive no servidor
 * (`situacaoContaComoAverbado`) e ate 04/09/2026 nao aparecia em lugar nenhum
 * da tela.
 *
 * Fica DENTRO do `data-print-scope`, e nao no `no-print`: um PDF que sai sem a
 * abrangencia e' pior que a tela, porque circula fora do contexto onde alguem
 * poderia perguntar.
 */
function SecaoHeader({ titulo, abrangencia, onExcel, onPdf }: { titulo: string; abrangencia?: string; onExcel: () => void; onPdf: () => void }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
      <div style={{ display: "grid", gap: 3 }}>
        <span style={eyebrow}>{titulo}</span>
        {abrangencia ? (
          <span style={{ fontSize: 11.5, color: "var(--text-muted)", lineHeight: 1.4, maxWidth: 460 }}>
            {abrangencia}
          </span>
        ) : null}
      </div>
      <div className="no-print" style={{ display: "flex", gap: 6 }}>
        <Button size="sm" variant="ghost" onClick={onExcel} title="Baixar em Excel (CSV/UTF-8)">↓ Excel</Button>
        <Button size="sm" variant="ghost" onClick={onPdf} title="Imprimir / salvar como PDF">↓ PDF</Button>
      </div>
    </div>
  );
}

const eyebrow: React.CSSProperties = { fontSize: 11, fontWeight: 700, letterSpacing: ".08em", color: "var(--text-dim)", textTransform: "uppercase" };
